var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));

// src/widget/getAppConfig.ts
import { matchPath } from "react-router-dom";
var PreviewType = {
  TESTING: "testing-preview",
  STYLES: "styles-preview",
  LIVE: "live_preview"
};
var DEFAULT_ENDPOINTS = {
  CMS: "https://widgetstate-dev.votenow.tv/v3/state/",
  CONNECT: "https://voteapi-dev.votenow.tv/s2/vote",
  PREVIEW: "https://ssp-api-dev.votenow.tv/v1/widget_previews/"
};
var WIDGETSTATE_PRESETS = {
  [PreviewType.TESTING]: {
    pollingRate: null
  },
  [PreviewType.STYLES]: {
    pollingRate: null
  },
  [PreviewType.LIVE]: {
    pollingRate: 1e3
  }
};
var getAppConfig = () => {
  const tsReactInitConfig = window.tsReactInitConfig || {};
  const {
    widgetStateUrl = DEFAULT_ENDPOINTS.CMS,
    connectUrl = DEFAULT_ENDPOINTS.CONNECT,
    previewUrl = DEFAULT_ENDPOINTS.PREVIEW,
    apiHash = void 0
  } = tsReactInitConfig;
  const getPreviewType = () => {
    var _a;
    const pattern = apiHash ? "/:param/" : "/:widgetId/:param/";
    const match2 = matchPath({ path: pattern, end: false }, window.location.pathname);
    const param = (_a = match2 == null ? void 0 : match2.params) == null ? void 0 : _a.param;
    if (!param || !Object.values(PreviewType).includes(param)) {
      return null;
    }
    if (param === PreviewType.STYLES) {
      const searchParams = new URLSearchParams(window.location.search);
      const isLivePreview = searchParams.get("mode") === PreviewType.LIVE;
      if (isLivePreview)
        return PreviewType.LIVE;
    }
    return param;
  };
  const previewType = getPreviewType();
  const isPreviewUrl = !!previewType;
  const getBasenamePattern = () => {
    const BASE_URLS = {
      DEFAULT: "/",
      PREVIEW: "/:previewType/:previewHash"
    };
    const basePattern = isPreviewUrl ? BASE_URLS.PREVIEW : BASE_URLS.DEFAULT;
    return apiHash ? basePattern : `/:widgetId${basePattern.replace(/\/$/, "")}`;
  };
  const basenamePattern = getBasenamePattern();
  const match = matchPath({ path: basenamePattern, end: false }, window.location.pathname);
  const urlParams = match == null ? void 0 : match.params;
  const basename = urlParams ? Object.entries(urlParams).reduce(
    (acc, [key, value]) => acc.replace(`:${key}`, value),
    basenamePattern
  ) : "/";
  const getAppHash = () => {
    if (isPreviewUrl && (urlParams == null ? void 0 : urlParams.previewHash))
      return urlParams.previewHash;
    return apiHash || (urlParams == null ? void 0 : urlParams.widgetId) || "";
  };
  const appHash = getAppHash();
  const widgetUrl = isPreviewUrl ? previewUrl : widgetStateUrl;
  const widgetId = (urlParams == null ? void 0 : urlParams.widgetId) || apiHash;
  const widgetStatePresets = previewType ? WIDGETSTATE_PRESETS[previewType] : null;
  const isDevMode = !!previewType && previewType !== PreviewType.TESTING;
  return {
    isPreviewUrl,
    basenamePattern,
    basename,
    urlParams,
    appHash,
    widgetUrl,
    widgetStatePresets,
    widgetId,
    isDevMode,
    connectUrl
  };
};

// src/browser/memoryStorage.ts
var MemoryStorage = class {
  constructor() {
    this._length = 0;
    this.storage = /* @__PURE__ */ new Map();
  }
  /**
   * The getItem() method of the Storage interface, when passed a key name, will return that key's value, or null if the key does not exist, in the given Storage object.
   * @param key A string containing the name of the key you want to retrieve the value of.
   * @returns A string containing the value of the key. If the key does not exist, null is returned.
   */
  getItem(key) {
    var _a;
    return (_a = this.storage.get(key)) != null ? _a : null;
  }
  /**
   * The setItem() method of the Storage interface, when passed a key name and value, will add that key to the given Storage object, or update that key's value if it already exists.
   * @param key A string containing the name of the key you want to create/update.
   * @param value A string containing the value you want to give the key you are creating/updating.
   */
  setItem(key, value) {
    this.storage.set(key, value);
    this._length++;
  }
  /**
   * The removeItem() method of the Storage interface, when passed a key name, will remove that key from the given Storage object if it exists.
   * @param key1 A string containing the name of the key you want to remove.
   */
  removeItem(key) {
    this.storage.delete(key);
    this._length--;
  }
  /**
   * The removeItems() method of the Storage interface, when passed multiple key names, will remove thsoe keys from the given Storage object if they exist.
   * @param keys keys1, keys2, ..., keysN
   */
  removeItems(...keys) {
    keys.forEach((key) => this.removeItem(key));
  }
  /**
   * The clear() method of the Storage interface clears all keys stored in a given Storage object.
   */
  clear() {
    this.storage.clear();
    this._length = 0;
  }
  /**
   * The key() method of the Storage interface, when passed a number n, returns the name of the nth key in a given Storage object.
   * The order of keys is maintained, because MemoryStorage uses Map internally to store the key value pairs.
   * @param index An integer representing the number of the key you want to get the name of. This is a zero-based index.
   * @returns
   */
  key(index) {
    const keys = this.storage.keys();
    return Array.from(keys)[index];
  }
  /**
   * The length read-only property of the Storage interface returns the number of data items stored in a given Storage object.
   */
  get length() {
    return this._length;
  }
};

// src/browser/prefixedStorage.ts
var PrefixedStorage = class {
  /**
   *
   * @param prefix Defaults to ''. A prefix to namespace all keys created when adding items.
   * @param storage Defaults to `localStorage`. An instance of a Storage object. When not supported, PrefixedStorage will fall back to MemoryStorage.
   */
  constructor(prefix = "", storage = null) {
    this.prefix = "";
    this.prefix = prefix;
    try {
      if (!storage) {
        storage = localStorage;
      }
      const testKey = "telescope_tv_test_key";
      storage.setItem(testKey, testKey);
      storage.removeItem(testKey);
      this.storage = storage;
    } catch (e) {
      this.storage = new MemoryStorage();
    }
  }
  /**
   * The getItem() method of the Storage interface, when passed a key name, will return that key's value, or null if the key does not exist, in the given Storage object.
   * @param key A string containing the name of the key you want to retrieve the value of.
   * @returns A string containing the value of the key. If the key does not exist, null is returned.
   */
  getItem(key) {
    const value = this.storage.getItem(this.prefix + key);
    try {
      if (value) {
        const item = JSON.parse(value);
        if (item && item.hasOwnProperty("expiration") && item.hasOwnProperty("value")) {
          const now = (/* @__PURE__ */ new Date()).getTime();
          if (item.expiration !== null && now >= item.expiration) {
            this.removeItem(key);
            return null;
          }
          return item.value;
        }
      }
    } catch (e) {
      return value;
    }
    return value;
  }
  /**
   * The setItem() method of the Storage interface, when passed a key name and value, will add that key to the given Storage object, or update that key's value if it already exists.
   * @param key A string containing the name of the key you want to create/update.
   * @param value A string containing the value you want to give the key you are creating/updating.
   */
  setItem(key, value, expiration = null) {
    const item = {
      value,
      expiration
    };
    this.storage.setItem(this.prefix + key, JSON.stringify(item));
  }
  /**
   * The removeItem() method of the Storage interface, when passed a key name, will remove that key from the given Storage object if it exists.
   * @param key1 A string containing the name of the key you want to remove.
   */
  removeItem(key) {
    this.storage.removeItem(this.prefix + key);
  }
  /**
   * The removeItems() method of the Storage interface, when passed multiple key names, will remove thsoe keys from the given Storage object if they exist.
   * @param keys keys1, keys2, ..., keysN
   */
  removeItems(...keys) {
    keys.forEach((key) => this.removeItem(key));
  }
  /**
   * The clear() method of the Storage interface clears all keys stored in a given Storage object.
   */
  clear() {
    this.storage.clear();
  }
  /**
   * The key() method of the Storage interface, when passed a number n, returns the name of the nth key in a given Storage object.
   * The order of keys is maintained, because MemoryStorage uses Map internally to store the key value pairs.
   * @param index An integer representing the number of the key you want to get the name of. This is a zero-based index.
   * @returns
   */
  key(index) {
    const key = this.storage.key(index);
    if (typeof key === "string") {
      return key.substring(this.prefix.length);
    }
    return key;
  }
  /**
   * The length read-only property of the Storage interface returns the number of data items stored in a given Storage object.
   */
  get length() {
    return this.storage.length;
  }
};
function createStorage(prefix = "") {
  return new PrefixedStorage(prefix);
}

// src/browser/fixedEncodeURIComponent.ts
var fixedEncodeURIComponent = (uriComponent) => encodeURIComponent(uriComponent).replace(/[!'()*]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);

// src/browser/convertToQspString.ts
var isValidParamValue = (value) => ["undefined", void 0, null, ""].indexOf(value) < 0;
var convertToQspString = (params, encodeValue = true) => {
  let result = "";
  for (const key in params) {
    const value = params[key];
    if (isValidParamValue(value)) {
      const qspValue = encodeValue ? fixedEncodeURIComponent(value) : `${value}`;
      result += `${key}=${qspValue.trim()}&`;
    }
  }
  return result.slice(0, -1);
};

// src/browser/getQSPs.ts
var getQSPs = () => {
  return window.location.search.slice(1).split("&").reduce((prev, cur) => {
    const [key, value = ""] = cur.split("=");
    if (key === "")
      return prev;
    return __spreadProps(__spreadValues({}, prev), { [key]: value });
  }, {});
};

// src/browser/getQueryParamByName.ts
var getQueryParamByName = (name) => {
  const regexS = `[\\?&]${name}=([^&#]*)`;
  const regex = new RegExp(regexS);
  const results = regex.exec(window.location.search);
  if (results === null)
    return "";
  return decodeURIComponent(results[1].replace(/\+/g, " "));
};

// src/browser/isWebview.ts
function isWebview() {
  const userAgent = window.navigator.userAgent.toLowerCase();
  const safari = /safari/.test(userAgent);
  const ios = /iphone|ipod|ipad/.test(userAgent);
  const wv = /\bwv\b/.test(userAgent);
  if (ios) {
    return !safari;
  }
  return wv;
}

// src/api/createHash.ts
import Base64 from "crypto-js/enc-base64.js";
import hmacSHA256 from "crypto-js/hmac-sha256.js";
var createHash = (message, key) => {
  const hash = hmacSHA256(message, key);
  return Base64.stringify(hash);
};

// src/object/object.ts
var sortObjectByKeys = (unsortedObj) => {
  return Object.keys(unsortedObj).sort().reduce((acc, key) => {
    acc[key] = unsortedObj[key];
    return acc;
  }, {});
};

// src/string/string.ts
var TRUTHY_VALUES = [true, 1, "true", "1", "enabled", "yes", "active"];
var isTruthy = (value) => {
  return TRUTHY_VALUES.includes(value.toLowerCase().trim());
};

// src/array/sortArrayByProp.ts
var SortDirection = {
  ASCENDING: 1,
  DESCENDING: -1
};
var sortArrayByProp = (arr, key, direction = SortDirection.ASCENDING) => {
  return [...arr].sort((a, b) => {
    const aValue = typeof key(a) === "number" ? key(a).toString() : key(a);
    const bValue = typeof key(b) === "number" ? key(b).toString() : key(b);
    const result = aValue.localeCompare(bValue, void 0, { numeric: true });
    return direction * result;
  });
};
export {
  DEFAULT_ENDPOINTS,
  MemoryStorage,
  PrefixedStorage,
  PreviewType,
  SortDirection,
  WIDGETSTATE_PRESETS,
  convertToQspString,
  createHash,
  createStorage,
  fixedEncodeURIComponent,
  getAppConfig,
  getQSPs,
  getQueryParamByName,
  isTruthy,
  isWebview,
  sortArrayByProp,
  sortObjectByKeys
};
